import { useQuery } from '@apollo/client'
import {
  AppBar,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import * as notificationGraphql from '../../graphql/notifications'
import {
  findEntityTypeFromRole,
  getEntityCode,
  getRoleFromStorage
} from '../../helpers/userData'
import { AdminPanel } from './AdminPanel'
import { DeliveryList } from './DeliveryList'
import { InboxPanel } from './InboxPanel'
import { PreferencesPanel } from './PreferencesPanel'
import {
  NotificationCapabilities,
  NotificationSurface,
  NotificationTabKey,
  buildNotificationTabs
} from './notificationAccess'
import { ErrorState, t } from './shared'
import useStyles from './styles/index.styles'

/**
 * One surface's notification screens.
 *
 * `personal` (the default, mounted at /notifications) is every user's own
 * inbox, history and preferences. `management` (/notification-management) is
 * estate configuration for admins. They were a single nine-tab strip, which
 * gave most users six tabs they could never open and put two audiences on one
 * page.
 */
export default function Notifications({
  surface = 'personal'
}: {
  surface?: NotificationSurface
} = {}) {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { data, loading, error, refetch } = useQuery(
    notificationGraphql.NOTIFICATION_CAPABILITIES_QUERY
  )
  const capabilities: NotificationCapabilities =
    data?.notificationCapabilities ?? {
      adminRead: false,
      adminManage: false,
      providerRead: false,
      providerManage: false,
      globalScope: false
    }
  const tabs = useMemo(
    () => buildNotificationTabs(capabilities, surface),
    [capabilities, surface]
  )
  // The first tab of THIS surface, not a hardcoded 'inbox' — that key does not
  // exist on the management surface, so hardcoding it would select a tab whose
  // panel never renders.
  const [tab, setTab] = useState<NotificationTabKey | undefined>()
  const activeTab =
    tab && tabs.some((item) => item.key === tab) ? tab : tabs[0]?.key
  const currentScope = useMemo(() => {
    const type = findEntityTypeFromRole(getRoleFromStorage())
    const code = getEntityCode()
    return type && code ? { type: type.toUpperCase(), code } : undefined
  }, [])

  useEffect(() => {
    const fallback = tabs[0]?.key
    if (!tabs.some((item) => item.key === tab) && tab !== fallback) {
      setTab(fallback)
    }
  }, [tab, tabs])

  // Above the early returns, not inside the success branch.
  //
  // PageHeader renders nothing visible and sets document.title. Placed below
  // the guards it only ran once capabilities resolved, so the browser tab kept
  // the app default while loading and stayed wrong on the error path — which is
  // exactly when a user is most likely to be hunting through tabs. The test
  // asserting the title on the error path is what surfaced it.
  const header = <PageHeader title={t('Notifications')} />

  if (loading)
    return (
      <>
        {header}
        <CircularProgress aria-label={t('Loading notification access')} />
      </>
    )
  if (error)
    return (
      <>
        {header}
        <ErrorState
          message="Unable to load notification access."
          onRetry={() => refetch()}
        />
      </>
    )

  if (surface === 'management' && tabs.length === 0) {
    return (
      <>
        {header}
        <ErrorState message="You do not have access to notification management." />
      </>
    )
  }

  return (
    <div className={classes.root}>
      {header}
      {/* No visible page title. SupportTabs and InventoryTabs render none —
          PageHeader sets document.title and returns null, and the tab strip
          names the section. An <h1> here is what made this page read as a
          different product. */}
      <AppBar position="static" color="transparent">
        {/* scrollable below sm rather than fullWidth: nine tabs divided
            equally at ~360px leaves ~40px each, and the Persian labels run
            longer still. */}
        <Tabs
          value={activeTab ?? false}
          onChange={(_, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant={isSmallScreen ? 'scrollable' : 'fullWidth'}
          scrollButtons="auto"
          aria-label={t('Notification sections')}>
          {tabs.map((item) => (
            <Tab
              disableRipple
              className={classes.tab}
              id={`notification-tab-${item.key}`}
              aria-controls={`notification-panel-${item.key}`}
              key={item.key}
              value={item.key}
              label={t(item.label)}
            />
          ))}
        </Tabs>
      </AppBar>
      <Box
        className={classes.tabPanel}
        id={`notification-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`notification-tab-${activeTab}`}>
        {activeTab === 'inbox' && <InboxPanel />}
        {activeTab === 'history' && (
          <DeliveryList admin={false} canManage={false} heading="History" />
        )}
        {activeTab === 'preferences' && (
          <PreferencesPanel showHistory={capabilities.globalScope} />
        )}
        {activeTab &&
          !['inbox', 'history', 'preferences'].includes(activeTab) && (
            <AdminPanel
              tab={activeTab}
              capabilities={capabilities}
              currentScope={currentScope}
            />
          )}
      </Box>
    </div>
  )
}
